import React, { useMemo } from 'react';
import { Box, Select, SpaceBetween } from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { ProducerRun } from 'src/models/common/ExecutionRun';
import { CURRENT_NUMBERS_LABEL, ExecutionSelection, runLabel, SHARED_SELECTION } from './executionSelection';

const SHARED_VALUE = '__SHARED__';

interface ExecutionSelectorProps {
    runs: ProducerRun[];
    selection?: ExecutionSelection;
    onChange: (selection: ExecutionSelection) => void;
    disabled?: boolean;
    label?: string;
}

/** "Run" picker: Current numbers plus one option per run. Renders nothing until the producer has a run. */
export default function ExecutionSelector({
    runs,
    selection,
    onChange,
    disabled,
    label = 'Run',
}: ExecutionSelectorProps) {
    const options: SelectProps.Option[] = useMemo(
        () => [
            { label: CURRENT_NUMBERS_LABEL, value: SHARED_VALUE },
            ...runs.map((run) => ({ label: runLabel(run), value: run.executionId })),
        ],
        [runs]
    );
    if (runs.length === 0) return null;

    const selectedValue = selection?.kind === 'RUN' ? selection.run.executionId : SHARED_VALUE;
    return (
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>
                {label}
            </Box>
            <Select
                selectedOption={options.find((o) => o.value === selectedValue) ?? null}
                onChange={({ detail }) => {
                    const run = runs.find((r) => r.executionId === detail.selectedOption.value);
                    onChange(run ? { kind: 'RUN', run } : SHARED_SELECTION);
                }}
                options={options}
                placeholder={CURRENT_NUMBERS_LABEL}
                disabled={disabled}
                ariaLabel={label}
            />
        </SpaceBetween>
    );
}
